import { useEffect, useRef, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { updateSessionActivity, isSessionStale, getUniqueSessionKey } from '@/utils/device-session';
import { useAuth } from '@/contexts/AuthContext';

export const useSessionHealth = () => {
  const { user } = useAuth();
  const [isHealthy, setIsHealthy] = useState(true);
  const [conflictDetected, setConflictDetected] = useState(false);
  const healthCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const sessionRecoveryRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!user) return;

    const sessionKey = getUniqueSessionKey(user.id);
    
    // Set up health monitoring
    const startHealthMonitoring = () => {
      // Update activity immediately
      updateSessionActivity();
      
      // Set up periodic health checks
      healthCheckIntervalRef.current = setInterval(async () => {
        try {
          // Update activity timestamp
          updateSessionActivity();
          
          // Check if session is still valid
          const { data: session, error } = await supabase.auth.getSession();
          
          if (error || !session.session) {
            console.warn('Session health check failed:', error);
            setIsHealthy(false);
            
            // Attempt session recovery
            if (!sessionRecoveryRef.current) {
              sessionRecoveryRef.current = setTimeout(async () => {
                try {
                  console.log('Attempting session recovery...');
                  const { error: refreshError } = await supabase.auth.refreshSession();
                  
                  if (!refreshError) {
                    console.log('Session recovered successfully');
                    setIsHealthy(true);
                    setConflictDetected(false);
                  } else {
                    console.error('Session recovery failed:', refreshError);
                  }
                } catch (e) {
                  console.error('Session recovery error:', e);
                } finally {
                  sessionRecoveryRef.current = null;
                }
              }, 2000);
            }
          } else {
            setIsHealthy(true);
            
            // Clear any pending recovery attempts
            if (sessionRecoveryRef.current) {
              clearTimeout(sessionRecoveryRef.current);
              sessionRecoveryRef.current = null;
            }
          }
          
          // Check for stale sessions
          if (isSessionStale()) {
            console.log('Stale session detected, refreshing...');
            updateSessionActivity();
          }
          
        } catch (error) {
          console.error('Health check error:', error);
          setIsHealthy(false);
        }
      }, 30000); // Check every 30 seconds
    };

    // Set up conflict detection via presence monitoring
    const conflictChannel = supabase.channel(`session_monitor_${user.id}`)
      .on('presence', { event: 'sync' }, () => {
        const state = conflictChannel.presenceState();
        const sessions = Object.keys(state);
        
        // If more than 3 sessions detected, there might be conflicts
        if (sessions.length > 3) {
          console.warn('Multiple sessions detected, potential conflict');
          setConflictDetected(true);
        } else {
          setConflictDetected(false);
        }
      })
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          conflictChannel.track({
            session_key: sessionKey,
            last_activity: Date.now(),
            user_agent: navigator.userAgent.substring(0, 50)
          });
        }
      });

    startHealthMonitoring();

    return () => {
      // Cleanup health monitoring
      if (healthCheckIntervalRef.current) {
        clearInterval(healthCheckIntervalRef.current);
        healthCheckIntervalRef.current = null;
      }
      
      if (sessionRecoveryRef.current) {
        clearTimeout(sessionRecoveryRef.current);
        sessionRecoveryRef.current = null;
      }
      
      // Cleanup conflict detection
      supabase.removeChannel(conflictChannel);
    };
  }, [user?.id]);

  const forceSessionRefresh = async () => {
    try {
      console.log('Force refreshing session...');
      const { error } = await supabase.auth.refreshSession();
      
      if (!error) {
        setIsHealthy(true);
        setConflictDetected(false);
        updateSessionActivity();
        return true;
      } else {
        console.error('Force refresh failed:', error);
        return false;
      }
    } catch (error) {
      console.error('Force refresh error:', error);
      return false;
    }
  };

  return {
    isHealthy,
    conflictDetected,
    forceSessionRefresh
  };
};